import React, { useEffect, useCallback, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { format, parseISO } from "date-fns";

import { HeaderTitle } from "../../../../components/HeaderTitle";
import { useNotification } from "../../../../hooks/useNotification";

import { useAuth } from "../../../../hooks/useAuth";
import { Gender } from "../../enum";
import { IFormUpdateAnimal } from "../../interface";
import { animalById, updateAnimal } from "../../../../services/animals";
import { useTranslate } from "../../../../hooks/useTranslate";
import { Box, CircularProgress, Collapse, Paper, Stack } from "@mui/material";
import { FormContainer } from "react-hook-form-mui";
import AnimalForm from "../../components/animal-form";

export interface OptionSelect {
  label: string;
  id: string;
}

export const EditAnimals: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { propertiesIds } = useAuth();

  const { translate } = useTranslate();
  const { errorNotification, successNotification } = useNotification();

  const [loading, setLoading] = useState(false);

  const schema = yup.object().shape({
    name: yup.string().required(translate("animals.name_required")),
    identifier: yup.string().required(translate("animals.identifier_required")),
    dob: yup.date(),
    // .required(translate("global.dob_required")),
    type: yup.string().required(translate("global.type_required")),
    internship: yup
      .string()
      .nullable()
      .default(null)
      .required(translate("animals.internship_required")),
    // .when("gender", {
    //   is: (gender: Gender) => gender !== Gender.MALE,
    //   then: (scm) => scm.required(translate("animals.internship_required")),
    // }),
    batch: yup.string(),
    flock: yup.string(),
    description: yup.string(),
    race: yup.string().nullable(),
    // .required(translate("animals.race_required")),
    weighing: yup.number().transform((v) => (Number.isNaN(v) ? null : +v)),
    // .required(translate("animals.weighing_required")),
    photo: yup.mixed(),
  });

  const form = useForm<IFormUpdateAnimal>({
    resolver: yupResolver(schema),
  });

  const values = form.watch();

  const submitForm = async (data: IFormUpdateAnimal) => {
    const {
      name,
      dob,
      identifier,
      internship,
      race,
      batch,
      description,
      flock,
      breeding_animal,
      situation,
    } = data;

    try {
      const formData = new FormData();

      if (batch) formData.append("batch", batch);
      if (flock) formData.append("flock", flock);

      if (data.gender === Gender.FEMALE) {
        formData.append("breeding_animal", "false");
      } else {
        formData.append("breeding_animal", breeding_animal ? "true" : "false");
      }

      if (internship) {
        formData.append("internship", internship);
      }

      formData.append("name", name);
      formData.append("farm_id", propertiesIds[0]);
      formData.append("gender", data.gender);

      formData.append("number_identifier", identifier);

      if (dob) {
        formData.append("date_birth", format(dob, "yyyy-MM-dd"));
      }

      if (race) {
        formData.append("race", race);
      }

      if (situation) {
        formData.append("situation", situation);
      }
      // formData.append("date_weighing", String(dateWeighingFormatted));
      // formData.append("weighing", String(weighing));
      formData.append("description", description ?? "");

      formData.append("type", data.type);

      if (data.photo && data?.photo instanceof File) {
        formData.append("photo", data.photo);
      }

      await updateAnimal(params.id!, formData);
      successNotification(translate("animals.success_updated_animal"));
      navigate("/animals");
    } catch (error) {
      console.log(error);
      errorNotification(translate("animals.error_updated_animal"));
    }
  };

  const fetchAnimal = useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        const animal = await animalById(id);

        form.reset({
          ...animal,
          name: animal.name,
          weighing: animal?.weighing?.[0]?.weight,
          description: animal.description,
          identifier: String(animal.number_identifier),
          dob: animal.date_birth
            ? format(parseISO(animal.date_birth), "yyyy-MM-dd")
            : null,
          date_weighing: animal.date_weighing ?? null,
          gender: String(animal.gender).toLocaleLowerCase(),
          lactation: animal.lactation?.id,
          situation: animal.situation?.id,
          race: animal.race?.id,
          internship: animal.internship?.id,
          batch: animal.batch?.id,
          flock: animal.flock?.id,
        });

        setLoading(false);
      } catch (err) {
        console.error(err);
        errorNotification(translate("global.error_batch_or_flock"));
      }
    },
    [errorNotification, translate, form]
  );

  const ready = useRef(false);

  useEffect(() => {
    if (params.id && !ready.current) {
      ready.current = true;
      fetchAnimal(params.id);
    }
  }, [params, fetchAnimal]);

  return (
    <Stack spacing={4}>
      <HeaderTitle
        title={translate("animals.title_update")}
        url="/animals"
        showIcon
      />
      <Paper sx={{ p: 3, position: "relative", mt: values.photo ? -3 : 0 }}>
        <FormContainer formContext={form} onSuccess={submitForm}>
          <Collapse in={loading} unmountOnExit>
            <Box display="flex" alignItems="center" justifyContent="center">
              <CircularProgress />
            </Box>
          </Collapse>
          <Collapse in={!loading} unmountOnExit>
            <AnimalForm isNew={false} property={propertiesIds[0]} />
          </Collapse>
        </FormContainer>
      </Paper>
    </Stack>
  );
  // return (
  //   <Container>
  //     <HeaderTitle
  //       title={translate("animals.title_update")}
  //       showIcon
  //       url="/animals"
  //     />
  //     <Form onSubmit={handleSubmit(submitForm)}>
  //       <Switch
  //         currentActive={genderAnimal}
  //         options={options}
  //         onChange={(value) => {
  //           setGenderAnimal(value as any);
  //         }}
  //       />
  //       <ContentForm>
  //         <Input
  //           {...register("name")}
  //           error={errors.name?.message}
  //           placeholder={translate("animals.placeholder_name_animal")}
  //           type="text"
  //         />
  //         <Input
  //           {...register("identifier")}
  //           error={errors.identifier?.message}
  //           placeholder={translate("animals.placeholder_number_identification")}
  //           type="text"
  //         />
  //         <Input
  //           {...register("dob")}
  //           error={errors.dob?.message}
  //           placeholder={translate("global.placeholder_dob")}
  //           type="date"
  //           defaultValue={formatDateToAPI(parseISO(animal.date_birth))}
  //         />
  //         <Controller
  //           name="internship"
  //           control={control}
  //           render={({ field }) => (
  //             <SelectInput
  //               {...field}
  //               defaultValue={stages.find(
  //                 (stage) => stage.id === animal.internship
  //               )}
  //               options={stages}
  //               placeholder={translate("animals.placeholder_internship")}
  //               error={errors.internship?.label?.message}
  //             />
  //           )}
  //         />
  //         <PictureAnimal>
  //           {preImage ? (
  //             <PicturePreview>
  //               <img src={String(preImage)} alt="Secected Property" />
  //             </PicturePreview>
  //           ) : (
  //             <NoPicturePreview>
  //               <FiCamera />
  //               <p>{translate("animals.add_image")}</p>
  //             </NoPicturePreview>
  //           )}
  //           <ControlsUploadImage>
  //             <div>
  //               <label htmlFor="upload-file">
  //                 {translate("animals.select_image")}
  //               </label>
  //               <input
  //                 onChange={(e) => {
  //                   submitFile(e.target.files);
  //                   e.target.value = "";
  //                 }}
  //                 accept="image/*"
  //                 id="upload-file"
  //                 type="file"
  //               />
  //             </div>
  //             {preImage && (
  //               <RemovePicture>
  //                 <FiX
  //                   onClick={() => {
  //                     setPreImage("");
  //                     setImage("");
  //                   }}
  //                 />
  //               </RemovePicture>
  //             )}
  //           </ControlsUploadImage>
  //         </PictureAnimal>
  //         <Controller
  //           name="batch"
  //           control={control}
  //           render={({ field }) => (
  //             <SelectInput
  //               {...field}
  //               options={batchs}
  //               placeholder={translate("animals.placeholder_batch")}
  //               defaultValue={batchs.find(
  //                 (batch) => batch.id === animal.batch?.id
  //               )}
  //             />
  //           )}
  //         />
  //         <Controller
  //           name="flock"
  //           control={control}
  //           render={({ field }) => (
  //             <SelectInput
  //               {...field}
  //               options={flocks}
  //               placeholder={translate("animals.placeholder_flock")}
  //               defaultValue={flocks.find(
  //                 (flock) => flock.id === animal.flock?.id
  //               )}
  //             />
  //           )}
  //         />
  //         <Controller
  //           name="race"
  //           control={control}
  //           render={({ field }) => (
  //             <SelectInput
  //               {...field}
  //               options={races}
  //               defaultValue={races.find((race) => race.id === animal.race)}
  //               placeholder={translate("animals.placeholder_race")}
  //               error={errors.race?.label?.message}
  //             />
  //           )}
  //         />
  //         {/* <Input
  //             {...register("date_weighing")}
  //             error={errors.date_weighing?.message}
  //             placeholder={translate("animals.placeholder_date_weighing")}
  //             type="date"
  //             defaultValue={formatDateToAPI(parseISO(animal.date_weighing))}
  //           />
  //           <Input
  //             {...register("weighing")}
  //             error={errors.weighing?.message}
  //             placeholder={translate("animals.placeholder_weighing")}
  //             type="number"
  //           /> */}
  //         <Input
  //           {...register("description")}
  //           placeholder={translate("animals.placeholder_description")}
  //           type="text"
  //         />
  //       </ContentForm>
  //       {genderAnimal === Gender.MALE && (
  //         <ContentBreeder>
  //           <div>
  //             <p>{translate("animals.breeding")}</p>
  //             <OptionBreeder
  //               defaultChecked={hasBreeder}
  //               onChange={(value) => {
  //                 setHasBreeder(value);
  //               }}
  //             />
  //           </div>
  //           <p>{translate("animals.description_breeding")}</p>
  //         </ContentBreeder>
  //       )}
  //       <ContentButton>
  //         <Button
  //           isLoading={loading}
  //           type="submit"
  //           label={translate("animals.btn_update")}
  //         />
  //       </ContentButton>
  //     </Form>
  //   </Container>
  // );
};
